import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS } from '@angular/common/http'
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core'
import { Title } from '@angular/platform-browser'
import {
    NgbDateAdapter,
    NgbDateParserFormatter,
    NgbDatepickerConfig,
    NgbModalConfig,
    NgbTooltipConfig
} from '@ng-bootstrap/ng-bootstrap'
import { NgSelectConfig } from '@ng-select/ng-select'
import { provideErrorTailorConfig } from '@ngneat/error-tailor'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { AngularSvgIconModule } from 'angular-svg-icon'
import {
    CustomDateParserFormatter,
    CustomDatepickerAdapter,
    DatepickerConfig,
    GUARANTEE_FRONT_LIB_TOKEN,
    NavigationService
} from 'guarantee-front-lib'
import { AppLanguages } from '../shared/constants/enums'
import { ConfirmModalModule } from './components/confirm-modal/confirm-modal.module'
import { LoaderSpinnerModule } from './components/loader-spinner/loader-spinner.module'
import { ToastContainerModule } from './components/toast-container/toast-container.module'
import { TITLE_STRATEGY } from './configs/app-config'
import { ERROR_TAILOR_CONFIG } from './configs/error-tailor-config'
import { GUARANTEE_FRONT_LIB_CONFIG } from './configs/guarantee-front-lib.config'
import { ModalConfig } from './configs/ngb-modal-config'
import { SelectConfig } from './configs/ngb-select-config'
import { TooltipConfig } from './configs/ngb-tooltip-config'
import { TRANSLATE_CONFIG } from './configs/translate-config'
import { AuthInterceptor } from './interceptors/auth.interceptor'
import { CachingInterceptor } from './interceptors/caching.interceptor'
import { EncodeHttpParamsInterceptor } from './interceptors/encode-http-params.interceptor'
import { ErrorResponseInterceptor } from './interceptors/error-response.interceptor'
import { LanguageInterceptor } from './interceptors/language.interceptor'
import { LoaderInterceptor } from './interceptors/loader.interceptor'
import { AppLanguageService } from './services/app-language.service'
import { AuthService } from './services/auth.service'
import { RequestCacheService } from './services/request-cache.service'

const initializeAppFactory = (inject: Injector): (() => Promise<boolean>) => {
    return (): Promise<boolean> =>
        new Promise((resolve) => {
            const titleService: Title = inject.get(Title)
            const translateService: TranslateService = inject.get(TranslateService)
            const authService: AuthService = inject.get(AuthService)
            const appLanguageService: AppLanguageService = inject.get(AppLanguageService)
            const appLanguage = appLanguageService.getAppLanguage()
            const navigationService: NavigationService = inject.get(NavigationService)

            titleService.setTitle(TITLE_STRATEGY.siteName)
            translateService.addLangs(Object.values(AppLanguages))
            translateService.use(appLanguage)
            authService.autoLogin()
            navigationService.init()
            resolve(true)
        })
}

@NgModule({
    imports: [
        CommonModule,
        AngularSvgIconModule.forRoot(),
        TranslateModule.forRoot(TRANSLATE_CONFIG),
        ConfirmModalModule,
        LoaderSpinnerModule,
        ToastContainerModule
    ],
    exports: [LoaderSpinnerModule, ToastContainerModule],
    providers: [
        { provide: RequestCacheService },
        { provide: NavigationService },
        { provide: APP_INITIALIZER, useFactory: initializeAppFactory, deps: [Injector], multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: EncodeHttpParamsInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorResponseInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
        { provide: NgbDateAdapter, useClass: CustomDatepickerAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
        { provide: NgbDatepickerConfig, useClass: DatepickerConfig },
        { provide: NgbModalConfig, useClass: ModalConfig },
        { provide: NgSelectConfig, useClass: SelectConfig },
        { provide: NgbTooltipConfig, useClass: TooltipConfig },
        { provide: GUARANTEE_FRONT_LIB_TOKEN, useValue: GUARANTEE_FRONT_LIB_CONFIG },
        provideErrorTailorConfig(ERROR_TAILOR_CONFIG)
    ]
})
export class CoreModule {}
